import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Camera, CaretLeft, FileArrowUp, IdentificationCard, ShieldCheck, Warning } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import MobileHeader from "../../components/MobileHeader";
import BottomNav from "../../components/BottomNav";
import { Badge, Button, Card, Input, Label } from "../../components/ui-kit";

const DOCUMENT_TYPES = [
  ["auto", "Detection automatique"],
  ["passport", "Passeport"],
  ["voter_card", "Carte d'electeur"],
  ["driver_license", "Permis de conduire"],
  ["national_id", "Carte nationale"],
  ["other", "Autre piece"],
];

const CONFIRM_FIELDS = [
  ["full_name", "Nom complet"],
  ["document_type", "Type de document"],
  ["document_number", "Numero de la piece"],
  ["birth_date", "Date de naissance"],
  ["birth_place", "Lieu de naissance"],
  ["nationality", "Nationalite"],
  ["address", "Adresse"],
  ["issued_at", "Date d'emission"],
  ["expires_at", "Date d'expiration"],
];

function dataUrlToFile(dataUrl, filename) {
  const [meta, data] = dataUrl.split(",");
  const mime = meta.match(/data:(.*?);/)?.[1] || "image/jpeg";
  const binary = atob(data);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return new File([bytes], filename, { type: mime });
}

export default function IdentityVerification() {
  const inputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const autoTimerRef = useRef(null);

  const [documentType, setDocumentType] = useState("auto");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [cameraOpen, setCameraOpen] = useState(false);
  const [autoStatus, setAutoStatus] = useState("");
  const [result, setResult] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/identity/verification")
      .then(({ data }) => {
        const verification = data.verification;
        setResult(verification);
        setForm(verification?.confirmed || verification?.extracted || {});
        if (verification?.confirmed?.document_type || verification?.extracted?.document_type) {
          setDocumentType(verification.confirmed?.document_type || verification.extracted?.document_type);
        }
      })
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    return () => stopCamera();
  }, []);

  const stopCamera = () => {
    if (autoTimerRef.current) clearTimeout(autoTimerRef.current);
    autoTimerRef.current = null;
    streamRef.current?.getTracks?.().forEach((track) => track.stop());
    streamRef.current = null;
    setCameraOpen(false);
    setAutoStatus("");
  };

  const captureFrame = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || !video.videoWidth) return null;
    const width = video.videoWidth;
    const height = video.videoHeight;
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, width, height);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.92);
    const captured = dataUrlToFile(dataUrl, `piece-identite-${Date.now()}.jpg`);
    setFile(captured);
    setPreview(dataUrl);
    stopCamera();
    toast.success("Photo capturee");
    return captured;
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment", width: { ideal: 1600 }, height: { ideal: 1000 } },
        audio: false,
      });
      streamRef.current = stream;
      setCameraOpen(true);
      setAutoStatus("Place la piece dans le cadre");
      setTimeout(() => {
        if (videoRef.current) videoRef.current.srcObject = stream;
      }, 0);
      autoTimerRef.current = setTimeout(() => {
        setAutoStatus("Capture en cours");
        captureFrame();
      }, 2600);
    } catch {
      toast.error("Camera non disponible");
    }
  };

  const pickFile = (selected) => {
    setFile(selected);
    setPreview(selected ? URL.createObjectURL(selected) : "");
    setResult(null);
    setForm({});
  };

  const submit = async () => {
    if (!file) {
      toast.error("Ajoute ou capture une photo de la piece");
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("document_type", documentType);
    fd.append("file", file);
    try {
      const { data } = await api.post("/identity/verify", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      setForm(data.extracted || {});
      toast.success("Lecture terminee");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const confirm = async () => {
    if (!result?.id) return toast.error("Lance d'abord la lecture");
    setConfirming(true);
    try {
      const { data } = await api.post("/identity/confirm", {
        verification_id: result.id,
        ...form,
        document_type: form.document_type || documentType,
      });
      setResult(data);
      setForm(data.confirmed || {});
      toast.success("Identite enregistree");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setConfirming(false);
    }
  };

  const status = result?.status;
  const confirmed = status === "USER_CONFIRMED";
  const extracted = result?.extracted || {};

  return (
    <div className="phone-frame phone-scroll-safe bg-premium-grad">
      <MobileHeader title="Identite" />
      <div className="space-y-4 px-4 pt-2">
        <Link to="/app/profile" className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-primary">
          <CaretLeft size={14} weight="bold" /> Profil
        </Link>

        <Card className="p-5">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/15 text-primary">
              <IdentificationCard size={25} weight="duotone" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-heading text-2xl font-bold">Verification identite</div>
              <div className="text-xs text-muted-foreground">Capture, lecture, confirmation</div>
            </div>
            {status && <Badge tone={confirmed ? "success" : "warning"}>{confirmed ? "Validee" : "A confirmer"}</Badge>}
          </div>
        </Card>

        <Card className="p-5">
          <div className="mb-4">
            <Label>Type de piece</Label>
            <select
              value={documentType}
              onChange={(e) => setDocumentType(e.target.value)}
              className="w-full rounded-xl border border-white/10 bg-background px-4 py-3 text-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/40"
              disabled={confirmed}
            >
              {DOCUMENT_TYPES.map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>

          {cameraOpen ? (
            <div className="space-y-3">
              <div className="relative overflow-hidden rounded-2xl bg-black">
                <video ref={videoRef} autoPlay playsInline muted className="aspect-[4/3] w-full object-cover" />
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                  <div className="h-[58%] w-[86%] rounded-xl border-2 border-primary shadow-[0_0_0_999px_rgba(0,0,0,0.45)]" />
                </div>
                <div className="absolute inset-x-0 bottom-3 text-center text-xs font-bold uppercase tracking-widest text-white">
                  {autoStatus}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Button variant="outline" onClick={stopCamera}>Annuler</Button>
                <Button onClick={captureFrame}><Camera weight="fill" /> Capturer</Button>
              </div>
            </div>
          ) : (
            <div className="grid gap-3">
              <Button onClick={startCamera} disabled={confirmed} className="w-full">
                <Camera weight="fill" /> Capturer avec la camera
              </Button>
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                disabled={confirmed}
                className="flex w-full items-center gap-3 rounded-2xl border border-dashed border-white/15 bg-white/5 p-4 text-left press-down disabled:opacity-60"
              >
                <FileArrowUp size={22} weight="bold" className="text-primary" />
                <div className="min-w-0">
                  <div className="truncate text-sm font-bold">{file?.name || result?.original_filename || "Importer une photo"}</div>
                  <div className="text-[11px] text-muted-foreground">Photo nette, document entier visible</div>
                </div>
              </button>
              <input
                ref={inputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={(e) => pickFile(e.target.files?.[0] || null)}
              />
            </div>
          )}

          {preview && (
            <div className="mt-4 overflow-hidden rounded-2xl border border-white/10">
              <img src={preview} alt="Piece capturee" className="max-h-60 w-full object-cover" />
            </div>
          )}
          <canvas ref={canvasRef} className="hidden" />

          <Button onClick={submit} disabled={uploading || loading || confirmed || !file} className="mt-4 w-full">
            {uploading ? "Lecture..." : "Lire la piece"}
          </Button>
        </Card>

        {result && (
          <Card className="p-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                {confirmed ? (
                  <ShieldCheck size={22} weight="fill" className="text-secondary" />
                ) : (
                  <Warning size={22} weight="fill" className="text-[hsl(var(--warning))]" />
                )}
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-widest text-primary">Confirmation</div>
                  <div className="font-heading text-lg font-bold">Verifie les informations</div>
                </div>
              </div>
              <Badge tone={confirmed ? "success" : "warning"}>{confirmed ? "OK" : "A verifier"}</Badge>
            </div>

            <div className="mt-3 grid gap-3">
              {CONFIRM_FIELDS.map(([key, label]) => (
                <div key={key}>
                  <Label>{label}</Label>
                  <Input
                    value={form[key] || ""}
                    placeholder={extracted[key] || ""}
                    onChange={(e) => setForm((current) => ({ ...current, [key]: e.target.value }))}
                    disabled={confirmed}
                  />
                </div>
              ))}
            </div>

            {result.missing_fields?.length > 0 && !confirmed && (
              <div className="mt-3 rounded-xl border border-warning/30 bg-warning/10 p-3 text-xs">
                Complete les champs manquants avant validation.
              </div>
            )}

            <Button onClick={confirm} disabled={confirming || confirmed} className="mt-4 w-full" variant={confirmed ? "secondary" : "primary"}>
              <ShieldCheck weight="fill" /> {confirmed ? "Identite enregistree" : confirming ? "Enregistrement..." : "Confirmer et enregistrer"}
            </Button>
          </Card>
        )}
      </div>
      <BottomNav role="customer" />
    </div>
  );
}
